/**
 * GlobalDevicesService - Fetches device templates from global_devices table
 * 
 * Provides search/filter functionality for the global device library.
 * Read-only access to the community device database.
 */

export function createGlobalDevicesService() {
    let authService = null;
    let cachedManufacturers = [];
    let manufacturersCacheTime = 0;
    const MANUFACTURERS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

    function setAuthService(auth) {
        authService = auth;
    }

    function getClient() {
        return authService?.getSupabaseClient();
    }

    /**
     * Get all unique manufacturers from global devices
     * @returns {Promise<Array<string>>} Array of manufacturer names
     */
    async function getManufacturers() {
        // Return cached if fresh
        if (cachedManufacturers.length > 0 && Date.now() - manufacturersCacheTime < MANUFACTURERS_CACHE_TTL_MS) {
            return cachedManufacturers;
        }

        const client = getClient();
        if (!client) {
            console.warn('[GlobalDevices] No Supabase client available');
            return [];
        }

        try {
            // Use RPC function to get distinct manufacturers efficiently
            const { data, error } = await client.rpc('get_distinct_manufacturers');

            if (error) {
                console.error('[GlobalDevices] getManufacturers error:', error);
                return [];
            }

            // Extract manufacturer strings from the result
            const manufacturers = (data || [])
                .map(row => row.manufacturer)
                .filter(m => m && m.trim());

            cachedManufacturers = manufacturers;
            manufacturersCacheTime = Date.now();
            return manufacturers;
        } catch (e) {
            console.error('[GlobalDevices] getManufacturers exception:', e);
            return [];
        }
    }

    /**
     * Search global devices with manufacturer and/or device name filters
     * @param {Object} options - Search options
     * @param {string} options.manufacturer - Manufacturer filter (exact match)
     * @param {string} options.deviceName - Device name search (partial match)
     * @param {number} options.limit - Maximum results to return
     * @returns {Promise<Array>} Array of device templates
     */
    async function searchAdvanced({ manufacturer = '', deviceName = '', limit = 20 } = {}) {
        const client = getClient();
        if (!client) {
            console.warn('[GlobalDevices] No Supabase client available');
            return [];
        }

        try {
            let queryBuilder = client
                .from('global_devices')
                .select('id, name, manufacturer, version, definition')
                .eq('status', 'approved')
                .order('name', { ascending: true })
                .limit(limit);

            // Filter by manufacturer if provided
            if (manufacturer && manufacturer.trim()) {
                queryBuilder = queryBuilder.ilike('manufacturer', manufacturer.trim());
            }

            // Filter by device name if provided
            if (deviceName && deviceName.trim()) {
                const searchTerm = `%${deviceName.trim()}%`;
                queryBuilder = queryBuilder.ilike('name', searchTerm);
            }

            const { data, error } = await queryBuilder;

            if (error) {
                console.error('[GlobalDevices] searchAdvanced error:', error);
                return [];
            }

            // Transform to template format
            return (data || []).map(device => ({
                id: device.id,
                name: device.name,
                manufacturer: device.manufacturer,
                version: device.version,
                ...device.definition,
                isGlobal: true
            }));
        } catch (e) {
            console.error('[GlobalDevices] searchAdvanced exception:', e);
            return [];
        }
    }

    /**
     * Get default devices to show (no search criteria)
     * @param {number} limit - Number of devices to return
     * @returns {Promise<Array>} Array of device templates
     */
    async function getDefault(limit = 20) {
        return searchAdvanced({ limit });
    }

    /**
     * Search global devices with optional query (legacy method)
     * @param {string} query - Search query (searches name and manufacturer)
     * @param {number} limit - Maximum results to return
     * @returns {Promise<Array>} Array of device templates
     */
    async function search(query = '', limit = 100) {
        const client = getClient();
        if (!client) {
            console.warn('[GlobalDevices] No Supabase client available');
            return [];
        }

        try {
            let queryBuilder = client
                .from('global_devices')
                .select('id, name, manufacturer, version, definition')
                .eq('status', 'approved')
                .order('name', { ascending: true })
                .limit(limit);

            // Add search filter if query provided
            if (query && query.trim()) {
                const searchTerm = `%${query.trim().toLowerCase()}%`;
                queryBuilder = queryBuilder.or(`name.ilike.${searchTerm},manufacturer.ilike.${searchTerm}`);
            }

            const { data, error } = await queryBuilder;

            if (error) {
                console.error('[GlobalDevices] Search error:', error);
                return [];
            }

            // Transform to template format
            return (data || []).map(device => ({
                id: device.id,
                name: device.name,
                manufacturer: device.manufacturer,
                version: device.version,
                ...device.definition,  // Merge the definition JSONB
                isGlobal: true  // Mark as from global library
            }));
        } catch (e) {
            console.error('[GlobalDevices] Search exception:', e);
            return [];
        }
    }

    /**
     * Get a single device by ID
     * @param {string} deviceId - Device UUID
     * @returns {Promise<Object|null>} Device template or null
     */
    async function getById(deviceId) {
        const client = getClient();
        if (!client) return null;

        try {
            const { data, error } = await client
                .from('global_devices')
                .select('id, name, manufacturer, version, definition')
                .eq('id', deviceId)
                .single();

            if (error || !data) {
                console.warn('[GlobalDevices] getById error:', error);
                return null;
            }

            return {
                id: data.id,
                name: data.name,
                manufacturer: data.manufacturer,
                version: data.version,
                ...data.definition,
                isGlobal: true
            };
        } catch (e) {
            console.error('[GlobalDevices] getById exception:', e);
            return null;
        }
    }

    /**
     * Get total count of approved devices
     * @returns {Promise<number>} Count of devices
     */
    async function getCount() {
        const client = getClient();
        if (!client) return 0;

        try {
            const { count, error } = await client
                .from('global_devices')
                .select('*', { count: 'exact', head: true })
                .eq('status', 'approved');

            if (error) {
                console.warn('[GlobalDevices] getCount error:', error);
                return 0;
            }

            return count || 0;
        } catch (e) {
            console.error('[GlobalDevices] getCount exception:', e);
            return 0;
        }
    }

    return {
        setAuthService,
        search,
        searchAdvanced,
        getDefault,
        getManufacturers,
        getById,
        getCount
    };
}
